import { ApolloProvider } from '@apollo/client';
import Honeybadger from '@honeybadger-io/js';
import App, { AppContext, AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { getSelectorsByUserAgent } from 'react-device-detect';

import CrispWrapper from '@components/CrispWrapper';
import MaintenanceNotification from '@components/GlobalHeader/MaintenanceNotification';
import PopupBanner from '@components/PopupBanner';
import SpinWidget from '@components/SpinWidget/SpinWidget';
import ZaloChatWidget from '@components/ZaloWidget/ZaloWidget';
import AuthController from '@context/AuthContext';
import CompanyProvider from '@context/CompanyContext';
import AppLayoutController from '@context/LayoutContext';
import MaintenanceProvider from '@context/MaintenanceContext';
import WarrantyAuthController from '@context/WarrantyAuthContext';
import WarrantyPointController from '@context/WarrantyPointContext';
import { CartController } from '@hooks/cart';
import { CartController as CartController_v2 } from '@hooks/cart_v2';
import { CampaignsController } from '@hooks/giftCampaigns/controller';
import useApollo from '@hooks/useApollo';
import useStorageChange from '@hooks/useStorageChange';
import { fireCustomPageViewEvent } from '@utils/gtmUtils';
import { getUserAgent } from '@utils/userAgent';
import { appWithTranslation, useTranslation } from 'next-i18next';
import Cookies from 'universal-cookie';
import nextI18NextConfig from '../next-i18next.config.js';
import FeatureFlagsProvider from '@context/FeatureFlagContext';

require('@styles/global.less');

declare global {
  interface Window {
    Honeybadger: any;
    $crisp: any;
    CRISP_WEBSITE_ID: string;
    CRISP_TOKEN_ID: string;
    CRISP_COOKIE_EXPIRE: number;
    dataLayer: Record<string, any>[];
  }
}

Honeybadger.configure({
  apiKey: process.env.NEXT_PUBLIC_HONEYBADGER_API_KEY,
  revision: process.env.NEXT_PUBLIC_BUILD_VERSION || 'main',
  environment: process.env.NEXT_APP_ENV,
  developmentEnvironments: ['dev'],
  async: true,
  breadcrumbsEnabled: true,
});

if (typeof window !== 'undefined') {
  // This is only for non-production environment since it's easy to check Honeybadger
  window.Honeybadger = Honeybadger;
}

function CustomApp({
  Component,
  pageProps,
  isMobile,
}: AppProps & {
  isMobile: boolean;
}) {
  const cookies = new Cookies();
  const router = useRouter();
  const apolloClient = useApollo(pageProps);
  const [isFirst, setIsFirst] = useState<boolean>(true);

  const { i18n } = useTranslation();
  useEffect(() => {
    // Handle track custom_page_view event
    const pageUrl = router.asPath;
    const pageTitle = document.title;
    fireCustomPageViewEvent({ pageUrl, pageTitle });
  }, [router]);

  // We will reload the website if the user uses the browser's back button to change the language
  useEffect(() => {
    if (!isFirst) {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [router.locale]);

  useEffect(() => {
    const currentLang = cookies.get('language');
    if (currentLang && i18n.language !== currentLang.locale) {
      const { pathname, asPath, query } = router;
      router.push({ pathname, query }, asPath, { locale: currentLang.locale });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
    setIsFirst(false);
  }, []);

  useStorageChange('user', () => {
    window.location.reload();
  });

  return (
    <>
      <Head>
        <link rel="icon" href="/images/favicon.png" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <ZaloChatWidget />
      <ApolloProvider client={apolloClient}>
        <MaintenanceProvider>
          <FeatureFlagsProvider>
            <MaintenanceNotification />
            <AuthController>
              <WarrantyAuthController>
                <WarrantyPointController>
                  <CompanyProvider>
                    <AppLayoutController initialIsMobile={isMobile}>
                      <CartController_v2>
                        <CartController>
                          <CampaignsController>
                            <CrispWrapper>
                              <SpinWidget />
                              <PopupBanner />
                              <Component {...pageProps} />
                            </CrispWrapper>
                          </CampaignsController>
                        </CartController>
                      </CartController_v2>
                    </AppLayoutController>
                  </CompanyProvider>
                </WarrantyPointController>
              </WarrantyAuthController>
            </AuthController>
          </FeatureFlagsProvider>
        </MaintenanceProvider>
      </ApolloProvider>
    </>
  );
}

CustomApp.getInitialProps = async (appContext: AppContext) => {
  // we can't caculate window size in server to check viewport is mobile or not
  // so we need to check by user agent in request header
  const userAgent = getUserAgent(appContext.ctx);
  const selectors = getSelectorsByUserAgent(userAgent) || { isMobile: false };
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);

  return { ...appProps, isMobile: selectors.isMobile };
};

export default appWithTranslation(CustomApp, nextI18NextConfig);
